import React, {useEffect, useState} from 'react';
import { Box, Button, Divider, IconButton, Modal, Paper, Stack, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CustomTextInput from '../../../components/CustomTextInput';
import SelectedItems from '../../../components/SelectedItems';
import { useCreateUserHandler } from '../dataHandlers/createUserHandler';
import { useUpdateUserHandler } from '../dataHandlers/updateUserHandler';
import { useGetRolesHandler } from '../dataHandlers/getRolesHandler';
import { useGetUserHandler } from '../dataHandlers/getUserHandler';
import RoleSelect from './RoleSelect';

export default function InviteUserDialog({ open, onClose, user, mode }) {

  const schema = Yup.object().shape({
    name: Yup.string().required( 'This field is required'),
    email: Yup.string()
      .email( 'Email must be a valid email')
      .required('This field is required'),
    role_ids: Yup.array().of(Yup.string()),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { name: '', email: '', role_ids: [] },
  });

  const { mutate: createUser, isPending: isCreating } = useCreateUserHandler({ onSuccess: onClose });
  const { mutate: updateUser, isPending: isUpdating } = useUpdateUserHandler({ onSuccess: onClose });
  const { roles, isLoading: isRolesLoading } = useGetRolesHandler({
    searchQuery: { page: 0, size: 100 },
    enabled: open,
  });
  const { user: userDetails, isLoading: isUserLoading } = useGetUserHandler({
    userId: user?.id,
    enabled: open && mode === 'edit' && Boolean(user?.id),
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    setRolesError(false);
    if (mode === 'edit' && userDetails) {
      setSelectedRoles(userDetails.role_ids ?? []);
      reset({
        name: userDetails.name || '',
        email: userDetails.email || '',
        role_ids: selectedRoles || '',
      });
      return;
    }

    if (mode === 'create') {
      reset({
        name: '',
        email: '',
        role_ids: [],
      });
    }
  }, [open, mode, userDetails, reset]);

  const [selectedRoles, setSelectedRoles] = useState([]);
  const [rolesError, setRolesError] = useState(false);
  const onSubmit = (data) => {
    if(selectedRoles?.length === 0){
      setRolesError(true);
      reset({
        name: userDetails.name || '',
        email: userDetails.email || '',
        role_ids: ([]).map(String),
      });
      return;
    }
    if (mode === 'edit' && user?.id) {
      updateUser({ userId: user.id, name: data.name, email: data.email, role_ids: selectedRoles });
      return;
    }
    createUser({ name: data.name, email: data.email, role_ids: selectedRoles });
  };
  const handleRolesChange = (ids) => {
    setSelectedRoles(ids);
    setRolesError(false);
  };

  const isPending = isCreating || isUpdating;
  const isFormLoading = mode === 'edit' && isUserLoading;
  const title = mode === 'edit' ? 'Edit User' : 'Invite User';
  const submitLabel = isPending
    ? 'Processing...'
    : mode === 'edit'
      ?  'Save'
      : 'Invite User';

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          p: 2,
          outline: 'none',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Stack component="form" spacing={2} sx={{ mt: 2 }} onSubmit={handleSubmit(onSubmit)}>
          <CustomTextInput name="name" label={'Name'} control={control} errors={errors} />
          <CustomTextInput name="email" label={'Email'} disabled={mode === 'edit'} control={control} errors={errors} />

              <Box>
                <Typography mb={1}>{'Roles'}</Typography>
                <RoleSelect
                  roles={roles}
                  errors={rolesError}
                  selected={selectedRoles}
                  isLoading={isRolesLoading || isFormLoading}
                  onChange={handleRolesChange}
                  placeholder={'Select roles'}
                  selectedCountLabel={'{count} role(s) selected'}
                />
                <SelectedItems
                  items={selectedRoles}
                  getLabel={(id) => roles.find((role) => role.id === id)?.name ?? id}
                  onRemove={(id) => handleRolesChange((selectedRoles ?? []).filter((value) => value !== id))}
                />
              </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" variant="contained" disabled={isPending || isFormLoading}>
              {submitLabel}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Modal>
  );
}
